import { useMemo } from "react";
import {
  useAssignOrgMemberRole,
  useRemoveOrgMember,
} from "@/app/org-members/api/hooks";
import type { OrgMember } from "@/app/org-members/types";
import { useOrgRoles } from "@/app/roles/api/hooks";
import {
  RoleSwitcher,
  type RoleSwitcherOption,
} from "@/app/roles/components/role-switcher";
import { ROLE } from "@/app/roles/types";
import { useSession } from "@/lib/auth-client";

const ORG_ADMIN_KEY = ROLE.orgAdmin;
// Synthetic option: "no org-level role". Selecting it revokes the org binding.
const MEMBER_KEY = "member";

export function OrgMemberRoleSwitcher({ member }: { member: OrgMember }) {
  const { roles } = useOrgRoles();
  const { mutateAsync: assignRole, isPending: isAssigning } =
    useAssignOrgMemberRole();
  const { mutateAsync: removeRole, isPending: isRemoving } =
    useRemoveOrgMember();
  const { data: session } = useSession();

  const options = useMemo<RoleSwitcherOption[]>(() => {
    const orgAdminLabel =
      roles.find((role) => role.key === ORG_ADMIN_KEY)?.display_name ??
      "Org Admin";
    return [
      { key: ORG_ADMIN_KEY, label: orgAdminLabel },
      { key: MEMBER_KEY, label: "Member" },
    ];
  }, [roles]);

  const applyRole = (key: string) =>
    key === ORG_ADMIN_KEY
      ? assignRole({ accountId: member.account_id, role: ORG_ADMIN_KEY })
      : removeRole({ accountId: member.account_id });

  return (
    <RoleSwitcher
      currentKey={member.org_role?.key ?? MEMBER_KEY}
      dialogTitle="Change organization role"
      isPending={isAssigning || isRemoving}
      onConfirm={applyRole}
      options={options}
      selfDemotionKey={MEMBER_KEY}
      selfDemotionMessage="You'll lose organization admin access and may not be able to restore it yourself."
      subject={{
        isCurrentUser: session?.user.accountId === member.account_id,
        email: member.email,
      }}
      triggerClassName="w-[150px]"
    />
  );
}
